import { Modal, ModalBody, ROLE } from "baseui/modal";
import { styled } from "baseui";
import { useEffect } from 'react';

const StyledModal = styled(Modal, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ModalContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  padding: '32px 24px',
  backgroundColor: 'white',
  borderRadius: '20px',
  position: 'relative',
  width: '100%',
  maxWidth: '320px',
  minHeight: '240px',
  justifyContent: 'center',
  margin: '0 auto'
});

const ModalText = styled('div', {
  fontSize: '18px',
  fontWeight: 600,
  textAlign: 'center',
  color: '#000000',
  lineHeight: '1.4'
});

const CloseButton = styled('button', {
  position: 'absolute',
  top: '12px',
  right: '12px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  ':hover': {
    opacity: 0.7
  }
});

const CheckIcon = () => (
  <svg width="64" height="64" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="28" cy="28" r="28" fill="#2cda94"/>
    <path 
      d="M24 35L41 18L44 21L24 41L12 29L15 26L24 35Z" 
      fill="white"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

type SuccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** SAM-1323: what was created; a pass is not a booking. */
  kind?: 'pass' | 'booking';
};

export const SuccessModal = ({ isOpen, onClose, kind = 'pass' }: SuccessModalProps) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      closeable={false}
      animate
      autoFocus
      role={ROLE.dialog}
      overrides={{
        Root: {
          style: {
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }
        },
        Dialog: {
          style: {
            position: 'relative',
            margin: 'auto',
            padding: 0,
            backgroundColor: 'transparent',
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px',
            borderBottomLeftRadius: '20px',
            borderBottomRightRadius: '20px',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            maxWidth: '320px'
          }
        },
        DialogContainer: {
          style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)'
          }
        }
      }}
    >
      <ModalContent>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
        <CheckIcon />
        <ModalText>
          {kind === 'booking' ? 'Booking created' : 'Pass created'}
        </ModalText>
      </ModalContent>
    </StyledModal>
  );
}; 